/**
 * Blob Storage Connector configuration component.
 * Specialized configuration for Blob Storage connector (Azure / AWS S3).
 */

import { useState } from 'react';
import { FormField, Input, Switch } from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

export interface BlobStorageConnectorConfigProps {
  settingsJson?: string;
  onChange?: (settingsJson: string) => void;
}

/**
 * Blob Storage Connector configuration component.
 */
export const BlobStorageConnectorConfig = ({
  settingsJson = '{}',
  onChange,
}: BlobStorageConnectorConfigProps) => {
  const [provider, setProvider] = useState<'azure' | 'aws'>('azure');
  const [defaultContainer, setDefaultContainer] = useState('');
  const [connectionString, setConnectionString] = useState('');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [useManagedIdentity, setUseManagedIdentity] = useState(false);

  const handleProviderChange = (newProvider: 'azure' | 'aws') => {
    setProvider(newProvider);
    const settings = JSON.parse(settingsJson || '{}');
    settings.provider = newProvider;
    onChange?.(JSON.stringify(settings));
  };

  return (
    <FormSection title="Storage Provider Configuration" description="Select a cloud provider and configure credentials.">
      <div className="flex flex-col gap-5">
        <FormField label="Select Provider">
          <div className="inline-flex w-full rounded-[0.65rem] border border-[var(--color-border-default)] overflow-hidden" role="group">
            <button
              type="button"
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                provider === 'azure'
                  ? 'bg-[var(--color-accent-primary)] text-white'
                  : 'bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle)]'
              }`}
              onClick={() => handleProviderChange('azure')}
            >
              Azure Blob Storage
            </button>
            <button
              type="button"
              className={`flex-1 px-4 py-2 text-sm font-medium border-l border-[var(--color-border-default)] transition-colors ${
                provider === 'aws'
                  ? 'bg-[var(--color-accent-primary)] text-white'
                  : 'bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle)]'
              }`}
              onClick={() => handleProviderChange('aws')}
            >
              AWS S3
            </button>
          </div>
        </FormField>

        {provider === 'azure' && (
          <>
            <div className="ui-form-choice-field">
              <div className="ui-form-choice-field__row">
                <div className="ui-form-choice-field__control">
                  <Switch
                    checked={useManagedIdentity}
                    onCheckedChange={(checked) => {
                      setUseManagedIdentity(checked as boolean);
                      const settings = JSON.parse(settingsJson || '{}');
                      settings.useManagedIdentity = checked;
                      onChange?.(JSON.stringify(settings));
                    }}
                  />
                </div>
                <div className="ui-form-choice-field__label-cell">
                  <span className="ui-form-choice-field__label">Use Managed Identity (Azure)</span>
                </div>
              </div>
            </div>
            {!useManagedIdentity && (
              <FormField label="Connection String" description="Azure Storage account connection string.">
                <Input
                  type="password"
                  value={connectionString}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setConnectionString(e.target.value);
                    const settings = JSON.parse(settingsJson || '{}');
                    settings.connectionString = e.target.value;
                    onChange?.(JSON.stringify(settings));
                  }}
                />
              </FormField>
            )}
            <FormField label="Default Container" description="Default container name.">
              <Input
                value={defaultContainer}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDefaultContainer(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.defaultContainer = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
            </FormField>
          </>
        )}

        {provider === 'aws' && (
          <>
            <div className="grid gap-5 sm:grid-cols-2">
              <FormField label="Access Key ID" required>
                <Input
                  value={accessKeyId}
                  onChange={(e) => {
                    setAccessKeyId(e.target.value);
                    const settings = JSON.parse(settingsJson || '{}');
                    settings.accessKeyId = e.target.value;
                    onChange?.(JSON.stringify(settings));
                  }}
                />
              </FormField>
              <FormField label="Secret Access Key" required>
                <Input
                  type="password"
                  value={secretAccessKey}
                  onChange={(e) => {
                    setSecretAccessKey(e.target.value);
                    const settings = JSON.parse(settingsJson || '{}');
                    settings.secretAccessKey = e.target.value;
                    onChange?.(JSON.stringify(settings));
                  }}
                />
              </FormField>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <FormField label="Region" description="AWS region (e.g., us-east-1).">
                <Input
                  value={region}
                  onChange={(e) => {
                    setRegion(e.target.value);
                    const settings = JSON.parse(settingsJson || '{}');
                    settings.region = e.target.value;
                    onChange?.(JSON.stringify(settings));
                  }}
                />
              </FormField>
              <FormField label="Default Bucket" description="Default S3 bucket name.">
                <Input
                  value={defaultContainer}
                  onChange={(e) => {
                    setDefaultContainer(e.target.value);
                    const settings = JSON.parse(settingsJson || '{}');
                    settings.defaultBucket = e.target.value;
                    onChange?.(JSON.stringify(settings));
                  }}
                />
              </FormField>
            </div>
          </>
        )}
      </div>
    </FormSection>
  );
};
